/**
 * Contrato do pipeline de certificados.
 *
 * A ideia central, em uma frase: **um programa decide, os outros so pintam.**
 *
 * Desenhar um certificado tem duas etapas. DECIDIR (onde cada coisa vai, com
 * que tamanho, cor, se aparece) e PINTAR (executar a instrucao pronta). Hoje o
 * sistema tem quatro programas que fazem as duas, e por isso divergem: a
 * complexidade toda mora em "decidir", e cada um implementou a sua versao.
 *
 * Aqui so o `resolve` decide. Ele recebe um snapshot e devolve uma lista de
 * `Primitive` — instrucoes burras, em pontos absolutos, cor em RGB, texto ja
 * quebrado em linhas. Os pintores (`renderSvg`, `renderPdf`) nao tomam nenhuma
 * decisao, entao nao tem como discordar um do outro.
 *
 * Quatro mecanismos garantem que nada some no caminho:
 *
 *   1. Toda primitiva carrega `src` — o elemento que a gerou. Um teste verifica
 *      que todo elemento habilitado produziu ao menos uma primitiva.
 *   2. Os pintores sao `switch` exaustivos com `assertNever` no default: uma
 *      sexta variante de Primitive quebra a compilacao dos DOIS.
 *   3. Cada pintor conta o que pintou e falha se o total nao bater.
 *   4. O gate de deploy rasteriza SVG e PDF e compara pixel a pixel.
 */

// ─── Identidade dos elementos ───────────────────────────────────────────────

/**
 * Os 14 elementos que o produtor manipula no editor.
 *
 * Os nomes sao os mesmos de hoje, de proposito: renomear obrigaria a uma tabela
 * de traducao a mais na migracao dos configs reais, sem ganho nenhum.
 */
export type ElementId =
  | 'title'
  | 'titleDivider'
  | 'introText'
  | 'participantName'
  | 'nameUnderline'
  | 'cpf'
  | 'participationText'
  | 'eventName'
  | 'metadataRow'
  | 'conclusionText'
  | 'signatures'
  | 'validationLabel'
  | 'validationUrl'
  | 'certificateCode';

/**
 * Ordem de desenho, explicita.
 *
 * Nunca derive isto de `Object.keys` do jsonb: a ordem das chaves de um jsonb
 * do Postgres nao e a ordem de insercao (jsonb normaliza por tamanho e depois
 * alfabeticamente), entao a pilha de desenho mudaria conforme o conteudo.
 */
export const ELEMENT_ORDER: readonly ElementId[] = [
  'title',
  'titleDivider',
  'introText',
  'participantName',
  'nameUnderline',
  'cpf',
  'participationText',
  'eventName',
  'metadataRow',
  'conclusionText',
  'signatures',
  'validationLabel',
  'validationUrl',
  'certificateCode',
] as const;

// ─── Unidades e cor ─────────────────────────────────────────────────────────

/**
 * Ponto tipografico (1/72"), ja quantizado a 3 casas decimais.
 *
 * O config guarda posicao em 0..1 (fracao da pagina, independente do tamanho
 * do papel). A conversao para pontos acontece em exatamente um lugar,
 * `layout.ts:toPt()`. Se voce esta vendo aritmetica de coordenada fora de
 * `pipeline/`, esta no arquivo errado.
 */
export type Pt = number;

/** Componentes 0..1. Nunca 0..255 — e o formato que o pdf-lib espera. */
export interface RGB {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export type AssetId = string;

// ─── Primitivas ─────────────────────────────────────────────────────────────

/**
 * De onde uma primitiva veio.
 *
 * Os 14 elementos editaveis, mais `background`. O fundo desenha uma primitiva
 * de verdade, mas nao e um elemento que o produtor posiciona — dar a ele o id
 * de um elemento real quebraria o teste de cobertura, porque um elemento
 * desligado apareceria como tendo desenhado.
 */
export type PrimitiveSource = ElementId | 'background';

interface PrimitiveBase {
  /** Elemento que gerou esta instrucao. E o que torna a cobertura verificavel. */
  readonly src: PrimitiveSource;
  /** 0..1. Ja resolvido: a regra "cor propria anula opacity" foi aplicada. */
  readonly opacity: number;
}

export interface RectPrimitive extends PrimitiveBase {
  readonly k: 'rect';
  readonly x: Pt;
  readonly y: Pt;
  readonly w: Pt;
  readonly h: Pt;
  readonly fill: RGB;
}

export interface ImagePrimitive extends PrimitiveBase {
  readonly k: 'image';
  readonly x: Pt;
  readonly y: Pt;
  readonly w: Pt;
  readonly h: Pt;
  readonly asset: AssetId;
}

export interface LinePrimitive extends PrimitiveBase {
  readonly k: 'line';
  readonly x1: Pt;
  readonly y1: Pt;
  readonly x2: Pt;
  readonly y2: Pt;
  readonly stroke: RGB;
  readonly w: Pt;
}

export interface PolyPrimitive extends PrimitiveBase {
  readonly k: 'poly';
  readonly pts: readonly (readonly [Pt, Pt])[];
  readonly fill: RGB;
}

/**
 * Uma linha de texto ja posicionada.
 *
 * `text` e uma LINHA, nunca um paragrafo: a quebra ja aconteceu no `resolve`.
 * `x`/`y` sao a origem do baseline — nao o centro, nao o topo. O alinhamento
 * ja foi aplicado, entao o pintor nao mede nada.
 */
export interface GlyphsPrimitive extends PrimitiveBase {
  readonly k: 'glyphs';
  readonly text: string;
  readonly font: FontRef;
  /** Corpo em pontos. */
  readonly size: Pt;
  readonly x: Pt;
  readonly y: Pt;
  /** Espacamento extra entre caracteres, em pontos. 0 = normal. */
  readonly charSpacing: Pt;
  readonly fill: RGB;
}

export type FontRef = 'inter-400' | 'inter-600' | 'sora-700';

/**
 * As cinco formas que um pintor precisa saber desenhar. Nada mais.
 *
 * Repare no que NAO existe aqui: nao ha `titleDivider`, nao ha `metadataRow`,
 * nao ha `signatures`. Elementos compostos sao decompostos pelo `resolve` —
 * um `metadataRow` vira varios `glyphs` e `line`. O pintor nunca precisa saber
 * o que um "metadataRow" significa.
 */
export type Primitive =
  | RectPrimitive
  | ImagePrimitive
  | LinePrimitive
  | PolyPrimitive
  | GlyphsPrimitive;

// ─── Diagnosticos ───────────────────────────────────────────────────────────

export type DiagnosticCode =
  /** Elemento habilitado que nao produziu nenhuma primitiva. Sempre um bug. */
  | 'ELEMENT_EMPTY'
  /** Texto nao coube nem no menor corpo permitido e foi truncado. */
  | 'TEXT_TRUNCATED'
  /** Corpo reduzido para caber na caixa. */
  | 'TEXT_SHRUNK'
  /** Code point fora do subset da fonte. */
  | 'GLYPH_UNSUPPORTED'
  /** Valor fora da faixa valida; foi limitado. */
  | 'VALUE_CLAMPED'
  /** Id de elemento desconhecido no config; descartado. */
  | 'UNKNOWN_ELEMENT'
  /** Config sem `elements`; posicoes reconstruidas do formato antigo. */
  | 'LEGACY_MIGRATED'
  /**
   * O mesmo campo aparece em dois lugares com valores diferentes: dentro de
   * `fields` (o que o editor escreve) e na raiz (resquicio). Vence `fields`.
   */
  | 'FIELD_CONFLICT'
  /**
   * Elemento ligado que o gate de `fields` suprimiu.
   *
   * Sao dois interruptores para a mesma coisa — `elements[id].enabled`, que o
   * produtor mexe arrastando, e `fields.showX`, que ele mexe numa lista de
   * caixas — e eles podem discordar. Acontece em 3 dos 7 configs reais.
   *
   * Nao e erro: o gate vence de proposito. Mas suprimir em silencio deixa o
   * produtor com um elemento visivel no editor e ausente no PDF, que e a forma
   * do defeito que originou este pipeline. Dizer qual interruptor venceu e o
   * minimo.
   */
  | 'FIELD_GATED'
  /** O documento do participante sai mascarado, independente do toggle. */
  | 'DOCUMENT_MASKED'
  /** Asset declarado mas ausente do manifesto. */
  | 'ASSET_MISSING'
  /** Mais signatarios do que o layout comporta. */
  | 'SIGNERS_OVERFLOW';

export type DiagnosticSeverity = 'info' | 'warn' | 'error';

export interface Diagnostic {
  readonly code: DiagnosticCode;
  readonly severity: DiagnosticSeverity;
  /** Elemento afetado, quando aplicavel. */
  readonly element?: ElementId;
  /** Mensagem em pt-BR, para exibir no editor. */
  readonly message: string;
}

// ─── Assets ─────────────────────────────────────────────────────────────────

export type AssetRole = 'background' | 'signature';

/**
 * Asset enderecado por CONTEUDO, nao por URL.
 *
 * O `id` e o sha256 dos bytes. Uma URL e mutavel: trocar o arquivo no storage
 * mudaria silenciosamente certificados ja emitidos. Com hash de conteudo, o
 * certificado aponta para bytes especificos e nada muda por baixo.
 */
export interface AssetRef {
  readonly id: AssetId;
  readonly role: AssetRole;
  readonly mime: 'image/png' | 'image/jpeg';
  readonly w: number;
  readonly h: number;
  /** Caminho no storage: `certificate-assets/<sha256>`. */
  readonly src: string;
}

export interface AssetManifest {
  readonly assets: readonly AssetRef[];
}

// ─── Snapshot ───────────────────────────────────────────────────────────────

/** Dados do certificado, congelados no momento da emissao. */
export interface CertificateData {
  readonly certificateCode: string;
  readonly verificationUrl: string;
  readonly attendeeName: string;
  /**
   * Documento SEMPRE mascarado (`***.456.789-**`).
   *
   * O certificado e alcancavel por URL publica com o codigo, e o produtor nao
   * tem autoridade para publicar CPF completo de terceiro numa pagina
   * verificavel (LGPD). O toggle do editor controla se o documento aparece,
   * nunca se ele aparece inteiro. Decisao registrada em 2026-08-27.
   */
  readonly attendeeDocumentMasked: string | null;
  readonly eventTitle: string;
  readonly eventStartsAt: string | null;
  readonly eventEndsAt: string | null;
  readonly eventDateMode: 'start' | 'end' | 'range';
  readonly venueName: string | null;
  readonly workloadHours: number | null;
  readonly organizerName: string | null;
  readonly checkedInAt: string | null;
}

export interface CertificateSnapshot {
  readonly v: number;
  readonly pipeline: string;
  /** ISO 8601. Vira o CreationDate do PDF — por isso `new Date()` e proibido. */
  readonly issuedAt: string;
  /** Timezone DO EVENTO, congelado. Nunca a do servidor nem a do leitor. */
  readonly timeZone: string;
  /** `events.certificate_config` cru, sem normalizar. */
  readonly configRaw: unknown;
  readonly data: CertificateData;
  readonly assets: readonly AssetRef[];
  readonly fonts: readonly string[];
}

// ─── Saida ──────────────────────────────────────────────────────────────────

export interface PageSize {
  readonly width: Pt;
  readonly height: Pt;
}

export interface ResolvedCertificate {
  readonly page: PageSize;
  readonly primitives: readonly Primitive[];
  readonly diagnostics: readonly Diagnostic[];
  /** Assets efetivamente referenciados. O renderer so precisa carregar estes. */
  readonly assetsUsed: readonly AssetId[];
  readonly pipelineVersion: string;
  /** ISO do snapshot, propagado para os metadados do PDF. */
  readonly issuedAt: string;
}

// ─── Exaustividade ──────────────────────────────────────────────────────────

/**
 * Chamado no `default:` de todo switch sobre `Primitive`.
 *
 * Se alguem adicionar uma sexta variante, o argumento deixa de ser `never` e
 * a compilacao quebra em TODOS os pintores ao mesmo tempo. E o que torna
 * "esqueci de implementar no PDF" impossivel em vez de improvavel.
 */
export function assertNever(x: never): never {
  throw new Error(
    `Primitiva nao tratada: ${JSON.stringify(x)}. ` +
      'Todo switch sobre Primitive precisa cobrir as cinco variantes.',
  );
}
